import * as React from "react";
import { useMeasure } from "react-use";
import styled from "styled-components";

const mapUrl = (uuid: string) => `https://maps.griptonite.io/${uuid}.svg`;

const Wrapper = styled.div`
  height: 100%;
  position: relative;
`;

type TransformFunction = (x: number, y: number) => [number, number];

type MapData = {
  uuid: string;
  width: number;
  height: number;
};

type Dimensions = {
  width: number;
  height: number;
  screenWidth: number;
  screenHeight: number;
};

interface MapObject {
  mapPosition: { x: number; y: number };
}

type Props = {
  map: MapData;
  zoomToBbox: boolean;
  maxZoom: number;
  mapObjects: MapObject[];
  bboxPadding?: number;
  renderMapObject: (
    mapObject: MapObject,
    transform: TransformFunction,
    dimensions: Dimensions
  ) => React.ReactNode;
};

const MapObjects: React.FC<{
  transform: TransformFunction;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapObjects: any[];
  dimensions: Dimensions;
  renderMapObject: (
    mapObject: MapObject,
    transform: TransformFunction,
    dimensions: Dimensions
  ) => React.ReactNode;
}> = ({ transform, mapObjects, renderMapObject, dimensions }) => (
  <div>{mapObjects.map((x) => renderMapObject(x, transform, dimensions))}</div>
);

const getBbox = (mapObjects: MapObject[], padding = 10) => {
  let y1 = Infinity;
  let y2 = -Infinity;
  let x1 = Infinity;
  let x2 = -Infinity;
  mapObjects.forEach((mapObject) => {
    y1 = Math.min(mapObject.mapPosition.y, y1);
    y2 = Math.max(mapObject.mapPosition.y, y2);
    x1 = Math.min(mapObject.mapPosition.x, x1);
    x2 = Math.max(mapObject.mapPosition.x, x2);
  });
  return {
    x1: x1 - padding,
    x2: x2 + padding,
    y1: y1 - padding,
    y2: y2 + padding,
  };
};

// const MapPinCollection: React.FC = ({children}) => {
//   return <div className="pins">{children}</div>;
// };

const Map: React.FC<Props> = ({
  map,
  mapObjects,
  zoomToBbox = true,
  bboxPadding = 30,
  maxZoom = 3,
  renderMapObject,
}) => {
  const [wrapperRef, { width, height }] = useMeasure<HTMLDivElement>();
  const [hasZoomed, setHasZoomed] = React.useState<boolean>(false);

  React.useEffect(() => {
    const t = setTimeout(() => setHasZoomed(true), 1000);
    return () => clearTimeout(t);
  }, []);

  const bbox = React.useMemo(() => getBbox(mapObjects, 0), [mapObjects]);

  // const bboxH = bbox.y2 - bbox.y1;
  // const bboxW = bbox.x2 - bbox.x1;

  let zoomBbox1: [number, number], zoomBbox2: [number, number];
  if (zoomToBbox && hasZoomed) {
    zoomBbox1 = [bbox.x1, bbox.y1];
    zoomBbox2 = [bbox.x2, bbox.y2];
  } else {
    zoomBbox1 = [0, 0];
    zoomBbox2 = [map.width, map.height];
  }
  const zoomBboxH = zoomBbox2[1] - zoomBbox1[1];
  const zoomBboxW = zoomBbox2[0] - zoomBbox1[0];

  // let scaleFactor = height / map.height;
  // if (scaleFactor * map.width >= width) {
  //   scaleFactor = width / map.width;
  // }
  // let translateX = width / 2 - (map.width * scaleFactor) / 2;
  // let translateY = height / 2 - (map.height * scaleFactor) / 2;
  // const coords = [136.83553222656252, 83.28556618239614];
  const maxScaleFactor =
    Math.min(
      (height - bboxPadding * 2) / map.height,
      (width - bboxPadding * 2) / map.width
    ) * maxZoom;
  const scaleFactor = Math.min(
    Math.min(
      (height - bboxPadding * 2) / zoomBboxH,
      (width - bboxPadding * 2) / zoomBboxW
    ),
    maxScaleFactor
  );

  // translate centre of bbox to centre of screen
  const translateX = width / 2 - (zoomBbox1[0] + zoomBboxW / 2) * scaleFactor;
  const translateY = height / 2 - (zoomBbox1[1] + zoomBboxH / 2) * scaleFactor;

  // if (zoomToBbox) {
  //   let bboxScaleFactor = map.height / (bbox2[1] - bbox1[1]);
  //   if (bboxScaleFactor * bboxW * scaleFactor > width) {
  //     bboxScaleFactor = map.width / (bbox2[0] - bbox1[0]);
  //   }
  //   scaleFactor *= Math.min(bboxScaleFactor, maxZoom);

  // }
  const coordTransform = (x: number, y: number): [number, number] => [
    x * scaleFactor + translateX,
    y * scaleFactor + translateY,
  ];

  // const asTranslate = (x: number, y: number) => `translate(${x}px, ${y}px)`;

  return (
    <Wrapper ref={wrapperRef}>
      {width === 0 || height === 0 ? null : (
        <>
          <svg style={{ width: "100%", height: "100%" }}>
            <g
              transform={`translate(${translateX}, ${translateY}) scale(${scaleFactor})`}
              style={
                hasZoomed ? { transition: "transform 500ms ease-in-out" } : {}
              }
            >
              <image x="0" y="0" href={mapUrl(map.uuid)} />
            </g>
          </svg>
          <MapObjects
            renderMapObject={renderMapObject}
            transform={coordTransform}
            mapObjects={mapObjects}
            dimensions={{
              screenWidth: width,
              screenHeight: height,
              width: map.width,
              height: map.height,
            }}
          />
        </>
      )}
      {/*<div
        style={{
          transform: `${asTranslate(
            ...coordTransform(coords[0], coords[1]),
          )} translate(-50%, -50%)`,
          position: 'absolute',
          left: 0,
          top: 0,
          backgroundColor: 'red',
          transition: hasZoomed && 'transform 500ms ease-in-out',
        }}>
        Hello world
      </div>
      <div
        style={{
          transform: asTranslate(...coordTransform(bbox1[0], bbox1[1])),
          width: bboxW * scaleFactor,
          height: bboxH * scaleFactor,
          border: '1px solid red',
          position: 'absolute',
          left: 0,
          top: 0,
          transition:
            hasZoomed &&
            'transform 500ms ease-in-out, width 500ms ease-in-out, height 500ms ease-in-out',
        }}></div>*/}
    </Wrapper>
  );
};

export default Map;
